import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  STORY_LIMITS,
  scenesInOrder,
  type ActOutline,
  type ChapterOutline,
  type SceneSummary,
  type StoryDeletionImpact,
  type StoryLevel,
  type StoryOutline,
  type StoryProject,
  type StoryProjectInput,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile, type StoryProjectView } from '../db/protocol.ts';
import { settings } from '../settings.ts';
import { SceneEditor, WritingLog } from './SceneEditor.tsx';

/**
 * Story Studio's outline (docs/PARITY-STEP-5-PLAN.md section 3), as the desktop's: the
 * realm's story projects as a gallery; an open project's acts, chapters and scenes as a
 * tree beside the page for whichever is chosen, with its notes, what it holds (moved with
 * ↑ and ↓) and, for a project, its progress toward its word goal. A scene's page steps
 * through the project in reading order with Previous and Next.
 */

const galleryRoute = getRouteApi('/_app/realm/$realmKey/story');
const projectRoute = getRouteApi('/_app/realm/$realmKey/story/$projectId');

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);
const number = (n: number) => n.toLocaleString('en');
const plural = (n: number, one: string, many: string) => `${number(n)} ${n === 1 ? one : many}`;

/** "2 acts, 3 chapters and 6 scenes (1,234 words)", for a deletion's confirmation. */
function describeImpact(impact: StoryDeletionImpact): string {
  const parts = [
    impact.acts > 0 && plural(impact.acts, 'act', 'acts'),
    impact.chapters > 0 && plural(impact.chapters, 'chapter', 'chapters'),
    impact.scenes > 0 && plural(impact.scenes, 'scene', 'scenes'),
  ].filter((p): p is string => Boolean(p));
  if (parts.length === 0) return '';
  const list = parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)!}`;
  return `${list} (${plural(impact.words, 'word', 'words')})`;
}

interface Deleting {
  readonly level: StoryLevel;
  readonly id: string;
  readonly name: string;
  readonly text: string;
}

/** Asks what goes with a deletion, then how to say it. */
async function deletionText(file: string, level: StoryLevel, id: string, name: string): Promise<string> {
  const impact = await realms.storyDeletionImpact(file, level, id);
  const held = describeImpact(impact);
  if (level === 'scene') return `Delete “${name}” (${plural(impact.words, 'word', 'words')})? Its earlier versions go with it. This can't be undone.`;
  let text = `Delete “${name}”?`;
  const verb = impact.acts + impact.chapters + impact.scenes === 1 ? 'goes' : 'go';
  text += held ? ` Its ${held} ${verb} with it` : ' It holds nothing yet';
  text += level === 'project' && impact.sittings > 0 ? `, and its writing log (${plural(impact.sittings, 'sitting', 'sittings')}).` : '.';
  return `${text} This can't be undone.`;
}

// ---- the gallery ----

export function StoryProjectsPage() {
  const projects = galleryRoute.useLoaderData();
  const { realmKey } = galleryRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [making, setMaking] = useState(false);
  const [deleting, setDeleting] = useState<Deleting | null>(null);
  const [error, setError] = useState('');

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      setDeleting(null);
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function askDelete(id: string, name: string) {
    if (!settings()['general.confirmDeletes']) return void run(() => realms.deleteStoryItem(file, 'project', id), 'The project could not be deleted.');
    try {
      setDeleting({ level: 'project', id, name, text: await deletionText(file, 'project', id, name) });
    } catch (e) {
      setError(message(e, 'The project could not be checked.'));
    }
  }

  return (
    <article className="document story-page">
      <p className="eyebrow">Story Studio</p>
      <h1>Story Studio</h1>
      <p className="lead">Write your novels, stories and scripts in the world they're set in: acts, chapters and scenes, with a word goal to aim for.</p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setMaking(true)}>
          New project
        </button>
      </div>
      {making && (
        <ProjectForm
          heading="New story project"
          submit="Make project"
          onCancel={() => setMaking(false)}
          onSave={(input) =>
            run(async () => {
              const { id } = await realms.createStoryProject(file, input);
              await navigate({ to: '/realm/$realmKey/story/$projectId', params: { realmKey, projectId: id }, search: {} });
            }, 'The project could not be made.')
          }
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {projects.length === 0 ? (
        <p className="meta">No story projects yet. Make one to start outlining.</p>
      ) : (
        <ul className="table-gallery story-gallery">
          {projects.map((p) => (
            <li key={p.id}>
              <Link to="/realm/$realmKey/story/$projectId" params={{ realmKey, projectId: p.id }} search={{}}>
                📖 {p.name}
              </Link>
              <span className="meta">
                {plural(p.actCount, 'act', 'acts')}, {plural(p.chapterCount, 'chapter', 'chapters')}, {plural(p.sceneCount, 'scene', 'scenes')}
                {p.writingGoalWords > 0 ? ` · goal ${plural(p.writingGoalWords, 'word', 'words')}` : ''}
              </span>
              <button type="button" className="quiet" onClick={() => void askDelete(p.id, p.name)}>
                Delete…
              </button>
              {p.description && <p className="pre-line story-description">{p.description}</p>}
              {deleting?.id === p.id && (
                <Confirm
                  label={`Delete ${p.name}`}
                  text={deleting.text}
                  button="Delete project"
                  onCancel={() => setDeleting(null)}
                  onConfirm={() => run(() => realms.deleteStoryItem(file, 'project', p.id), 'The project could not be deleted.')}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ---- a project ----

type Selection =
  | { readonly level: 'project' }
  | { readonly level: 'act'; readonly act: ActOutline }
  | { readonly level: 'chapter'; readonly act: ActOutline; readonly chapter: ChapterOutline }
  | { readonly level: 'scene'; readonly act: ActOutline; readonly chapter: ChapterOutline; readonly scene: SceneSummary };

/** Where the item is in the outline; the project itself when it isn't there (or is gone). */
function select(outline: StoryOutline, item: string | undefined): Selection {
  if (!item) return { level: 'project' };
  for (const act of outline.acts) {
    if (act.act.id === item) return { level: 'act', act };
    for (const chapter of act.chapters) {
      if (chapter.chapter.id === item) return { level: 'chapter', act, chapter };
      const scene = chapter.scenes.find((s) => s.id === item);
      if (scene) return { level: 'scene', act, chapter, scene };
    }
  }
  return { level: 'project' };
}

type Panel = 'none' | 'edit' | 'new';

interface Context {
  readonly view: StoryProjectView;
  readonly file: string;
  readonly realmKey: string;
  readonly panel: Panel;
  readonly setPanel: (panel: Panel) => void;
  readonly deleting: Deleting | null;
  readonly act: (work: () => Promise<unknown>, fallback: string) => Promise<void>;
  /** Asks to delete; `after` runs once it's gone. */
  readonly askDelete: (level: StoryLevel, id: string, name: string, after?: () => Promise<unknown>) => Promise<void>;
  readonly confirmDelete: () => Promise<void>;
  readonly cancelDelete: () => void;
  readonly open: (item: string | null) => Promise<void>;
  /** Back to all projects, once this one's deleted. */
  readonly leave: () => Promise<void>;
}

export function StoryProjectPage() {
  const view = projectRoute.useLoaderData();
  const search = projectRoute.useSearch();
  const { realmKey, projectId } = projectRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>('none');
  const [deleting, setDeleting] = useState<(Deleting & { after?: () => Promise<unknown> }) | null>(null);
  const [error, setError] = useState('');

  const back = (
    <Link to="/realm/$realmKey/story" params={{ realmKey }}>
      All story projects
    </Link>
  );
  if (!view) {
    return (
      <article className="document">
        <p className="meta">That story project no longer exists. It may have been deleted. {back}</p>
      </article>
    );
  }

  async function act(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      setPanel('none');
      setDeleting(null);
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  // Only while still on this project's page: you may have gone elsewhere since.
  const here = () => router.state.location.pathname.endsWith(`/story/${projectId}`);
  const open = async (item: string | null) => {
    if (here()) await navigate({ to: '/realm/$realmKey/story/$projectId', params: { realmKey, projectId }, search: item ? { item } : {} });
  };

  const leave = async () => {
    if (here()) await navigate({ to: '/realm/$realmKey/story', params: { realmKey } });
  };

  const remove = (level: StoryLevel, id: string, after?: () => Promise<unknown>) =>
    act(async () => {
      await realms.deleteStoryItem(file, level, id);
      await after?.();
    }, `The ${level} could not be deleted.`);

  async function askDelete(level: StoryLevel, id: string, name: string, after?: () => Promise<unknown>) {
    setPanel('none');
    if (!settings()['general.confirmDeletes']) return remove(level, id, after);
    try {
      setDeleting({ level, id, name, text: await deletionText(file, level, id, name), ...(after ? { after } : {}) });
    } catch (e) {
      setError(message(e, `The ${level} could not be checked.`));
    }
  }

  const context: Context = {
    view,
    file,
    realmKey,
    panel,
    setPanel: (p) => {
      setDeleting(null);
      setPanel(panel === p ? 'none' : p);
    },
    deleting,
    act,
    askDelete,
    confirmDelete: () => (deleting ? remove(deleting.level, deleting.id, deleting.after) : Promise.resolve()),
    cancelDelete: () => setDeleting(null),
    open,
    leave,
  };

  const { outline } = view;
  const selection = select(outline, search.item);
  // A new item or another page closes what was open: keyed on the item.
  const key = search.item ?? outline.project.id;

  return (
    <article className="document story-page">
      <p className="eyebrow">{back}</p>
      <h1>{outline.project.name}</h1>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <div className="chronology-layout">
        <nav className="timeline-tree story-outline" aria-label="Outline">
          <OutlineTree outline={outline} selected={search.item && selection.level !== 'project' ? search.item : outline.project.id} realmKey={realmKey} />
        </nav>
        <div key={key} className="story-view">
          {search.item && selection.level === 'project' && <p className="meta">That part of the outline no longer exists. It may have been deleted.</p>}
          {selection.level === 'project' && <ProjectDetail {...context} />}
          {selection.level === 'act' && <ActDetail {...context} outlineAct={selection.act} />}
          {selection.level === 'chapter' && <ChapterDetail {...context} chapter={selection.chapter} />}
          {selection.level === 'scene' && <SceneDetail {...context} chapter={selection.chapter} summary={selection.scene} />}
        </div>
      </div>
    </article>
  );
}

// ---- the tree ----

function OutlineTree({ outline, selected, realmKey }: { outline: StoryOutline; selected: string; realmKey: string }) {
  const projectId = outline.project.id;
  const link = (id: string, label: string, root = false) => (
    <Link
      to="/realm/$realmKey/story/$projectId"
      params={{ realmKey, projectId }}
      search={root ? {} : { item: id }}
      aria-current={id === selected ? 'page' : undefined}
      className={id === selected ? 'selected' : undefined}
    >
      {label}
    </Link>
  );
  return (
    <ul>
      <li>
        {link(projectId, outline.project.name, true)}
        {outline.acts.length > 0 && (
          <ul>
            {outline.acts.map(({ act, chapters }) => (
              <li key={act.id}>
                {link(act.id, act.title)}
                {chapters.length > 0 && (
                  <ul>
                    {chapters.map(({ chapter, scenes }) => (
                      <li key={chapter.id}>
                        {link(chapter.id, chapter.title)}
                        {scenes.length > 0 && (
                          <ul>
                            {scenes.map((scene) => (
                              <li key={scene.id}>{link(scene.id, scene.title)}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </li>
    </ul>
  );
}

// ---- the pages for each level ----

function ProjectDetail(context: Context) {
  const { view, file, panel, setPanel, act, askDelete } = context;
  const { project, acts, wordCount } = view.outline;
  return (
    <section aria-labelledby="story-heading">
      <h2 id="story-heading">{project.name}</h2>
      {project.description && <p className="pre-line">{project.description}</p>}
      <Progress words={wordCount} goal={project.writingGoalWords} />
      {project.sessionWordGoal > 0 && <p className="meta">Session goal: {plural(project.sessionWordGoal, 'word', 'words')} a sitting.</p>}
      <div className="actions">
        <button type="button" onClick={() => setPanel('new')}>
          New act
        </button>
        <button type="button" className="quiet" onClick={() => setPanel('edit')}>
          Edit project
        </button>
        <button type="button" className="quiet" onClick={() => void askDelete('project', project.id, project.name, context.leave)}
        >
          Delete project…
        </button>
      </div>
      {panel === 'edit' && (
        <ProjectForm heading="Edit project" submit="Save" initial={project} onCancel={() => setPanel('none')} onSave={(input) => act(() => realms.updateStoryProject(file, project.id, input), 'The project could not be saved.')} />
      )}
      {panel === 'new' && (
        <PartForm
          heading="New act"
          placeholder="e.g. Act I"
          submit="Make act"
          onCancel={() => setPanel('none')}
          onSave={(title, notes) => act(() => realms.createAct(file, project.id, title, notes), 'The act could not be made.')}
        />
      )}
      <DeleteHere context={context} id={project.id} />
      <h3>Writing log</h3>
      <WritingLog sittings={view.sittings} />
      <h3>Acts</h3>
      <Children
        context={context}
        level="act"
        none="No acts yet."
        items={acts.map(({ act: a, chapters }) => ({ id: a.id, title: a.title, meta: plural(chapters.length, 'chapter', 'chapters') }))}
      />
    </section>
  );
}

function ActDetail({ outlineAct, ...context }: Context & { outlineAct: ActOutline }) {
  const { file, panel, setPanel, act, askDelete, open } = context;
  const { act: a, chapters } = outlineAct;
  return (
    <section aria-labelledby="story-heading">
      <p className="eyebrow">Act</p>
      <h2 id="story-heading">{a.title}</h2>
      {a.notes && <p className="pre-line">{a.notes}</p>}
      <div className="actions">
        <button type="button" onClick={() => setPanel('new')}>
          New chapter
        </button>
        <button type="button" className="quiet" onClick={() => setPanel('edit')}>
          Edit act
        </button>
        <button type="button" className="quiet" onClick={() => void askDelete('act', a.id, a.title, () => open(null))}>
          Delete act…
        </button>
      </div>
      {panel === 'edit' && (
        <PartForm heading="Edit act" placeholder="e.g. Act I" submit="Save" initial={a} onCancel={() => setPanel('none')} onSave={(title, notes) => act(() => realms.updateAct(file, a.id, title, notes), 'The act could not be saved.')} />
      )}
      {panel === 'new' && (
        <PartForm
          heading="New chapter"
          placeholder="e.g. Chapter 1"
          submit="Make chapter"
          onCancel={() => setPanel('none')}
          onSave={(title, notes) => act(() => realms.createChapter(file, a.id, title, notes), 'The chapter could not be made.')}
        />
      )}
      <DeleteHere context={context} id={a.id} />
      <h3>Chapters</h3>
      <Children
        context={context}
        level="chapter"
        none="No chapters yet."
        items={chapters.map(({ chapter, scenes }) => ({ id: chapter.id, title: chapter.title, meta: plural(scenes.length, 'scene', 'scenes') }))}
      />
    </section>
  );
}

function ChapterDetail({ chapter: outlineChapter, ...context }: Context & { chapter: ChapterOutline }) {
  const { file, panel, setPanel, act, askDelete, open } = context;
  const { chapter, scenes } = outlineChapter;
  return (
    <section aria-labelledby="story-heading">
      <p className="eyebrow">Chapter</p>
      <h2 id="story-heading">{chapter.title}</h2>
      {chapter.notes && <p className="pre-line">{chapter.notes}</p>}
      <div className="actions">
        <button type="button" onClick={() => setPanel('new')}>
          New scene
        </button>
        <button type="button" className="quiet" onClick={() => setPanel('edit')}>
          Edit chapter
        </button>
        <button type="button" className="quiet" onClick={() => void askDelete('chapter', chapter.id, chapter.title, () => open(chapter.actId))}>
          Delete chapter…
        </button>
      </div>
      {panel === 'edit' && (
        <PartForm
          heading="Edit chapter"
          placeholder="e.g. Chapter 1"
          submit="Save"
          initial={chapter}
          onCancel={() => setPanel('none')}
          onSave={(title, notes) => act(() => realms.updateChapter(file, chapter.id, title, notes), 'The chapter could not be saved.')}
        />
      )}
      {panel === 'new' && (
        <SceneForm
          heading="New scene"
          submit="Make scene"
          onCancel={() => setPanel('none')}
          onSave={(title, goal) => act(() => realms.createScene(file, chapter.id, title, goal), 'The scene could not be made.')}
        />
      )}
      <DeleteHere context={context} id={chapter.id} />
      <h3>Scenes</h3>
      <Children
        context={context}
        level="scene"
        none="No scenes yet."
        items={scenes.map((s) => ({ id: s.id, title: s.title, meta: s.wordGoal > 0 ? `${number(s.wordCount)} / ${plural(s.wordGoal, 'word', 'words')}` : plural(s.wordCount, 'word', 'words') }))}
      />
    </section>
  );
}

function SceneDetail({ chapter, summary, ...context }: Context & { chapter: ChapterOutline; summary: SceneSummary }) {
  const { view, file, realmKey, act, askDelete, open } = context;
  const order = scenesInOrder(view.outline);
  const at = order.findIndex((s) => s.id === summary.id);
  if (!view.scene) return <p className="meta">That scene could not be opened. Try again.</p>;
  return (
    <SceneEditor
      key={view.scene.scene.id}
      file={file}
      realmKey={realmKey}
      project={view.outline.project}
      view={view.scene}
      navigation={{ at: at + 1, total: order.length, previous: order[at - 1]?.id ?? null, next: order[at + 1]?.id ?? null, open }}
      tools={
        <>
          <button
            type="button"
            className="quiet"
            title="Start a new scene at the end of this chapter"
            onClick={() =>
              void act(async () => {
                const { id } = await realms.createScene(file, chapter.chapter.id, 'New Scene', settings()['new.sceneWordGoal']);
                await open(id);
              }, 'The scene could not be made.')
            }
          >
            New scene after
          </button>
          <button type="button" className="quiet" onClick={() => void askDelete('scene', summary.id, summary.title, () => open(chapter.chapter.id))}>
            Delete scene…
          </button>
        </>
      }
      below={<DeleteHere context={context} id={summary.id} />}
    />
  );
}

// ---- shared parts ----

function Progress({ words, goal }: { words: number; goal: number }) {
  if (goal <= 0) return <p className="meta">{plural(words, 'word', 'words')}</p>;
  return (
    <p className="story-progress">
      <progress max={goal} value={Math.min(words, goal)} aria-label="Progress toward the word goal" />
      <span className="meta">
        {number(words)} / {plural(goal, 'word', 'words')}
        {words >= goal ? ' ✓' : ''}
      </span>
    </p>
  );
}

/** The confirmation for deleting the open item itself. */
function DeleteHere({ context, id }: { context: Context; id: string }) {
  const { deleting, confirmDelete, cancelDelete } = context;
  if (deleting?.id !== id) return null;
  return <Confirm label={`Delete ${deleting.name}`} text={deleting.text} button={`Delete ${deleting.level}`} onCancel={cancelDelete} onConfirm={confirmDelete} />;
}

/** An act's, chapter's or scene's children, in order, with ↑, ↓ and Delete. */
function Children({ context, level, items, none }: { context: Context; level: 'act' | 'chapter' | 'scene'; items: { id: string; title: string; meta: string }[]; none: string }) {
  const { file, realmKey, view, act, askDelete, deleting, confirmDelete, cancelDelete } = context;
  if (items.length === 0) return <p className="meta">{none}</p>;
  const move = (id: string, direction: -1 | 1) => act(() => realms.moveStoryItem(file, level, id, direction), `The ${level} could not be moved.`);
  return (
    <ol className="story-children">
      {items.map((item, i) => (
        <li key={item.id}>
          <Link to="/realm/$realmKey/story/$projectId" params={{ realmKey, projectId: view.outline.project.id }} search={{ item: item.id }}>
            {item.title}
          </Link>
          <span className="meta">{item.meta}</span>
          <span className="story-child-tools">
            <button type="button" className="quiet" disabled={i === 0} aria-label={`Move ${item.title} up`} title="Move up one place" onClick={() => void move(item.id, -1)}>
              ↑
            </button>
            <button type="button" className="quiet" disabled={i === items.length - 1} aria-label={`Move ${item.title} down`} title="Move down one place" onClick={() => void move(item.id, 1)}>
              ↓
            </button>
            <button type="button" className="quiet" aria-label={`Delete ${item.title}…`} title="Delete this one, and everything in it" onClick={() => void askDelete(level, item.id, item.title)}>
              Delete…
            </button>
          </span>
          {deleting?.id === item.id && <Confirm label={`Delete ${deleting.name}`} text={deleting.text} button={`Delete ${level}`} onCancel={cancelDelete} onConfirm={confirmDelete} />}
        </li>
      ))}
    </ol>
  );
}

// ---- forms ----

/** A goal typed as words: empty is none. */
const goalOf = (text: string) => (text.trim() === '' ? 0 : Number(text));
const goalText = (goal: number) => (goal > 0 ? String(goal) : '');

function ProjectForm({ heading, submit, initial, onSave, onCancel }: { heading: string; submit: string; initial?: StoryProject; onSave: (input: StoryProjectInput) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [goal, setGoal] = useState(goalText(initial?.writingGoalWords ?? 0));
  const [sessionGoal, setSessionGoal] = useState(goalText(initial?.sessionWordGoal ?? 0));
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ name, description, writingGoalWords: goalOf(goal), sessionWordGoal: goalOf(sessionGoal) });
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} required maxLength={STORY_LIMITS.name} autoFocus placeholder="e.g. Blood and Silver" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={STORY_LIMITS.notes} placeholder="Optional" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <label>
        Word goal
        <input type="number" min={0} max={STORY_LIMITS.goal} step={1} value={goal} placeholder="none" onChange={(e) => setGoal(e.currentTarget.value)} />
      </label>
      <label>
        Words a sitting
        <input type="number" min={0} max={STORY_LIMITS.goal} step={1} value={sessionGoal} placeholder="none" onChange={(e) => setSessionGoal(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

function PartForm({
  heading,
  placeholder,
  submit,
  initial,
  onSave,
  onCancel,
}: {
  heading: string;
  placeholder: string;
  submit: string;
  initial?: { title: string; notes: string };
  onSave: (title: string, notes: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(title, notes);
      }}
    >
      <h3>{heading}</h3>
      <label>
        Title
        <input value={title} required maxLength={STORY_LIMITS.name} autoFocus placeholder={placeholder} onChange={(e) => setTitle(e.currentTarget.value)} />
      </label>
      <label>
        Notes
        <textarea value={notes} rows={3} maxLength={STORY_LIMITS.notes} placeholder="Optional" onChange={(e) => setNotes(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

function SceneForm({ heading, submit, initial, onSave, onCancel }: { heading: string; submit: string; initial?: SceneSummary; onSave: (title: string, goal: number) => Promise<void>; onCancel: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? 'New Scene');
  const [goal, setGoal] = useState(goalText(initial ? initial.wordGoal : settings()['new.sceneWordGoal']));
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(title, goalOf(goal));
      }}
    >
      <h3>{heading}</h3>
      <label>
        Title
        <input value={title} required maxLength={STORY_LIMITS.name} autoFocus onFocus={(e) => e.currentTarget.select()} onChange={(e) => setTitle(e.currentTarget.value)} />
      </label>
      <label>
        Word goal
        <input type="number" min={0} max={STORY_LIMITS.goal} step={1} value={goal} placeholder="none" onChange={(e) => setGoal(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

function FormButtons({ submit, onCancel }: { submit: string; onCancel: () => void }) {
  return (
    <div className="actions">
      <button type="submit" className="primary">
        {submit}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<void>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
